/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  GamoraControllerContextIdentifier,
  GamoraFoundationGlueListenerType,
  GamoraFoundationNodeArgs,
  debugCreate,
} from '@ver.id/libs.internal.gamora';
import {
  HoruzProtocolChulakClientStateInvoker,
  HoruzProtocolChulakClientStateInvokerCompletedAuthenticationInput,
  HoruzProtocolChulakClientStateInvokerCreateAuthenticationResult,
} from './enums.js';
import { HoruzProtocolChulakClientStateBase } from './base.js';
import { HoruzProtocolChulakClientStateContext } from '../context.js';
import { HoruzProtocolChulakClientStateMap } from '../../statemap.js';
import { HoruzProtocolChulakDataType } from '../../data.js';
import { HoruzProtocolChulakErrorProtocolInvalidMessage } from '../../errors/protocol/invalid-message.error.js';
import { HoruzProtocolChulakMessageAuthCompletedGuard } from '../../guards/message/index.js';
import { HoruzProtocolChulakMessageAuthGuard } from '../../guards/message/auth.js';
import { IHoruz } from '../../../../horuz.js';
const debug = debugCreate({
  namespace: 'horuz:protocols:chulak:states:client:auth',
});

/**
 * Client
 *
 * Initialization state
 * @public
 */
export class HoruzProtocolChulakClientStateAuth extends HoruzProtocolChulakClientStateBase<
  typeof HoruzProtocolChulakClientStateMap,
  HoruzProtocolChulakClientStateContext,
  IHoruz
> {
  constructor(args: GamoraFoundationNodeArgs) {
    super(args);
  }

  /**
   * Event on message
   * @param contextIdentifier The context identifier
   */
  private _onQueueNext = async (
    clientStateContext: HoruzProtocolChulakClientStateContext,
  ) => {
    return this.$tryPostTask(clientStateContext, async () => {
      if (this.$hasPostedTransition(clientStateContext)) {
        return;
      }

      try {
        // Execute state
        const result = this.recv(clientStateContext);
        if (result.done === false) {
          // Decode message type
          switch (result.message.type) {
            case HoruzProtocolChulakDataType.AUTH_COMPLETED: {
              await this._onAuthCompletedMessage(
                clientStateContext,
                result.message,
              );
              break;
            }
            case HoruzProtocolChulakDataType.STATE_AGENT_START_ENTER: {
              this.$postTransition(clientStateContext, {
                stateNext: 'AgentStart',
              });
              break;
            }
            case HoruzProtocolChulakDataType.STATE_APP_PAIRING_ENTER: {
              this.$postTransition(clientStateContext, {
                stateNext: 'AppPairing',
              });
              break;
            }
            case HoruzProtocolChulakDataType.STATE_APP_PRESENTATION_ENTER: {
              this.$postTransition(clientStateContext, {
                stateNext: 'AppPresentation',
              });
              break;
            }
            default: {
              throw HoruzProtocolChulakErrorProtocolInvalidMessage.Create(
                'Message ' +
                  result.message.type +
                  ' not supported in Auth state.',
              );
            }
          }
        }
      } catch (e: any) {
        debug.trace(e?.message, e?.stack);
        this._tryHandleException(clientStateContext, e);
      }
    });
  };

  /**
   * Handle authentication
   */
  private async _onAuthCompletedMessage(
    clientStateContext: HoruzProtocolChulakClientStateContext,
    message: HoruzProtocolChulakMessageAuthCompletedGuard,
  ) {
    debug.log('(_onAuthCompletedMessage): Authentication completed. ');

    // Restored service session
    clientStateContext.stateContextRegistry.localServiceSession =
      message.serviceSession;

    // Execute complete invocation
    await this.$fireInvocation(
      clientStateContext.contextIdentifier,
      HoruzProtocolChulakClientStateInvoker.CompletedAuthentication,
      clientStateContext,
      {
        serviceSessionUuid: message.serviceSession.uuid,
        tags: clientStateContext.stateContextRegistry.localServiceSession.tags,
      } as HoruzProtocolChulakClientStateInvokerCompletedAuthenticationInput,
    );
  }

  /**
   * Send authentication
   */
  private async _sendAuthMessage(
    clientStateContext: HoruzProtocolChulakClientStateContext,
  ) {
    // Execute invocation
    const createAuthenticationResult =
      await this.$executeInvocation<HoruzProtocolChulakClientStateInvokerCreateAuthenticationResult>(
        clientStateContext.contextIdentifier,
        HoruzProtocolChulakClientStateInvoker.CreateAuthentication,
        clientStateContext,
      );

    // Create auth message
    const auth = HoruzProtocolChulakMessageAuthGuard.fromJSON({
      compact: createAuthenticationResult.compact,
      fresh: createAuthenticationResult.fresh,
    });

    debug.log('(_sendAuthMessage): Sending authentication request. ');

    // Authenticate over the
    await this.send(clientStateContext, auth);
  }

  /**
   * Enter state
   */
  override async $enter(
    clientStateContext: HoruzProtocolChulakClientStateContext,
  ): Promise<void> {
    try {
      // Authenticate over the websocket layer
      await this._sendAuthMessage(clientStateContext);

      // Listen for messages
      this.$root
        .$('queues')
        .$('data')
        .$on(clientStateContext.contextIdentifier, 'next', () =>
          this._onQueueNext(clientStateContext),
        );
    } catch (e: any) {
      debug.trace(e?.message, e?.stack);
      this._tryHandleException(clientStateContext, e);
    }
  }

  /**
   * Exit state
   */
  override async $exit(
    clientStateContext: HoruzProtocolChulakClientStateContext,
  ): Promise<void> {
    // Stop listening for messages
    this.$root
      .$('queues')
      .$('data')
      .$removeEventListenersByNamespaceAndName(
        clientStateContext.contextIdentifier,
        'next',
      );
  }

  /**
   * Register invokers
   */
  invoker(
    contextIdentifier: GamoraControllerContextIdentifier,
    name: typeof HoruzProtocolChulakClientStateInvoker.CreateAuthentication,
    listener: (
      clientStateContext: HoruzProtocolChulakClientStateContext,
    ) => Promise<HoruzProtocolChulakClientStateInvokerCreateAuthenticationResult>,
  ): this;
  invoker(
    contextIdentifier: GamoraControllerContextIdentifier,
    name: typeof HoruzProtocolChulakClientStateInvoker.CompletedAuthentication,
    listener: (
      clientStateContext: HoruzProtocolChulakClientStateContext,
      input: HoruzProtocolChulakClientStateInvokerCompletedAuthenticationInput,
    ) => Promise<void>,
  ): this;
  invoker<K extends keyof typeof HoruzProtocolChulakClientStateInvoker>(
    contextIdentifier: GamoraControllerContextIdentifier,
    name: K,
    listener: GamoraFoundationGlueListenerType,
  ): this {
    return super.$registerInvoker(contextIdentifier, name, listener);
  }
}
